<script
  lang="ts"
  setup
  generic="ColumnData extends CustomColumn[] | Readonly<CustomColumn[]>, RowDataType = any"
>
import { computed, useSlots, useTemplateRef, onMounted, type ComponentPublicInstance } from 'vue';
import ModernGrid from '@/common/components/molecules/modernGrid/ModernGrid.vue';
import { type Emit, type Props } from '@/common/components/molecules/grid/baseGrid.setup';
import { useBaseGridAdapter } from './baseGridAdapter.setup';
import { baseGridOption } from '@/common/utils/define';
import type { CustomColumn } from '@/common/utils/types';
import type { GridRawRowDataType, ModernGridExposed } from '../modernGrid/modernGrid.types';

// BaseGridAdapter 전용 추가 Props
interface AdapterProps {
  isTreeMode?: boolean;
  rows?: any[];
}

const props = withDefaults(
  defineProps<Omit<Props<ColumnData, RowDataType>, 'rows'> & AdapterProps>(),
  {
    type: 'page',
    rows: () => [],
    disabledRows: () => [],
    selectedRows: () => [],
    checkedRows: () => [],
    uncheckable: () => [],
    searchWord: undefined,
    option: () => structuredClone(baseGridOption),
    searchBarOption: () => ({
      use: false,
      mode: 'full',
      placeholder: '',
      useClientFilter: false,
      useMergedToolbar: false,
      filterSearch: {
        use: false,
      },
    }),
    tooltipOption: 'list',
    pagination: () => ({
      use: false,
      useClient: false,
      align: 'right',
      showSetting: false,
      showPageInfo: false,
      perData: 20,
      items: [20, 30, 40, 50],
      total: 0,
    }),
    exportExcelOption: () => ({}),
    hasDynamicColumns: false,
    customClass: () => ({}),
    isTreeMode: false,
  },
);

const emit = defineEmits<Emit<ColumnData, RowDataType>>();

const slots = useSlots();

const {
  // 변환된 Props (ModernGrid용)
  modernColumns,
  modernOption,
  modernSelectedRow,
  modernCheckedRows,
  modernExpandedRows,
  modernEnvKey,

  // 직접 전달 Props
  rows,
  disabledRows,
  uncheckableRows,
  tooltipOption,
  shownChipCount,
  highlightedRows,
  emptyMessage,
  searchEmptyMessage,
  searchWord,

  // Event Handlers
  handleUpdateSelectedRow,
  handleUpdateCheckedRows,
  handleUpdateExpandedRows,
  handleChangePagination,
  handleChangeFilter,
  handleChangeSorting,
  handleClickRow,
  handleDblClickRow,
  handleClickCell,
  handleClickCellChip,
  handleClickCellChipMore,
  handleExportToExcel,
  handleUpdateSearchWord,

  // Column 상태 변경 Handlers
  handleChangeColumnVisibility,
  handleChangeColumnOrder,
  handleChangeColumnSize,

  // Slot 유틸리티
  transformSlotItem,
  transformRowDetailItem,
  columnFieldNames,
} = useBaseGridAdapter(
  props as unknown as Props<ColumnData, GridRawRowDataType>,
  emit as unknown as Emit<ColumnData, GridRawRowDataType>,
  {
    get isTreeMode() {
      return props.isTreeMode;
    },
  },
);

const modernGridRef = useTemplateRef<ComponentPublicInstance & ModernGridExposed>('modernGridRef');

const customSlotNames = computed<string[]>(() => {
  return columnFieldNames.value.filter((fieldName) => {
    // 부모에서 해당 슬롯이 정의된 경우 rendererType보다 우선하여 포워딩
    return !!slots[fieldName];
  });
});

onMounted(() => {
  if (modernGridRef.value) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    emit('update:ref-obj', modernGridRef.value as any);
  }
});

defineExpose({
  forceResetEvGrid: () => {
    modernGridRef.value?.syncGridState?.();
  },
  get table() {
    return modernGridRef.value?.table;
  },
  syncGridState: () => {
    modernGridRef.value?.syncGridState?.();
  },
  syncRowSelection: () => {
    modernGridRef.value?.syncRowSelection?.();
  },
  exportExcel: () => {
    modernGridRef.value?.exportExcel?.();
  },
  resetSort: async () => {
    await modernGridRef.value?.resetSort?.();
  },
});
</script>

<template>
  <div :class="['base-grid-adapter', props.type]">
    <modern-grid
      ref="modernGridRef"
      :columns="modernColumns"
      :rows="rows"
      :option="modernOption"
      :dynamic-styles="props.dynamicStyles"
      :selected-row="modernSelectedRow"
      :checked-rows="modernCheckedRows"
      :expanded-rows="modernExpandedRows"
      :disabled-rows="disabledRows"
      :uncheckable-rows="uncheckableRows"
      :env-key="modernEnvKey"
      :tooltip-option="tooltipOption"
      :shown-chip-count="shownChipCount"
      :class="props.innerClass"
      :custom-class="props.customClass"
      :initial-search-word="searchWord"
      :highlighted-rows="highlightedRows"
      :original-columns="props.columns"
      :empty-message="emptyMessage"
      :search-empty-message="searchEmptyMessage"
      @update:selected-row="handleUpdateSelectedRow"
      @update:search-word="handleUpdateSearchWord"
      @update:checked-rows="handleUpdateCheckedRows"
      @update:expanded-rows="handleUpdateExpandedRows"
      @change-pagination="handleChangePagination"
      @change-filter="handleChangeFilter"
      @change-sorting="handleChangeSorting"
      @click-row="handleClickRow"
      @dblclick-row="handleDblClickRow"
      @click-cell="handleClickCell"
      @click-cell-chip="handleClickCellChip"
      @click-cell-chip-more="handleClickCellChipMore"
      @export-to-excel-from-server="handleExportToExcel"
      @change-column-visibility="handleChangeColumnVisibility"
      @change-column-order="handleChangeColumnOrder"
      @change-column-size="handleChangeColumnSize"
    >
      <!-- toolbar 슬롯 포워딩 -->
      <template
        v-if="$slots.toolbar"
        #toolbar
      >
        <slot name="toolbar" />
      </template>

      <!-- toolbarPostfix 슬롯 포워딩 -->
      <template
        v-if="$slots.toolbarPostfix"
        #toolbarPostfix
      >
        <slot name="toolbarPostfix" />
      </template>

      <!-- toolbarPrefix 슬롯 포워딩-->
      <template
        v-if="$slots.toolbarPrefix"
        #toolbarPrefix
      >
        <slot name="toolbarPrefix" />
      </template>

      <!-- subToolbar 슬롯 포워딩 -->
      <template
        v-if="$slots.subToolbar"
        #subToolbar
      >
        <slot name="subToolbar" />
      </template>

      <!-- topToolbar 슬롯 포워딩 -->
      <template
        v-if="$slots.topToolbar"
        #topToolbar
      >
        <slot name="topToolbar" />
      </template>

      <!-- customHeader 슬롯 포워딩 -->
      <template
        v-if="$slots.customHeader"
        #customHeader="{ item }"
      >
        <slot
          name="customHeader"
          :item="item"
        />
      </template>

      <!-- rowDetail 슬롯 포워딩 (BaseGrid 호환 형식으로 변환) -->
      <template
        v-if="$slots.rowDetail"
        #rowDetail="{ item }"
      >
        <slot
          name="rowDetail"
          :item="transformRowDetailItem(item)"
        />
      </template>

      <!-- tooltip-content 슬롯 포워딩 -->
      <template
        v-if="$slots['tooltip-content']"
        #tooltipContent
      >
        <slot name="tooltip-content" />
      </template>

      <!-- 커스텀 셀 슬롯 포워딩 (BaseGrid 형식으로 변환) -->
      <template
        v-for="slotName in customSlotNames"
        :key="`custom-slot-${slotName}`"
        #[slotName]="{ item }"
      >
        <slot
          :name="slotName"
          :item="transformSlotItem(item, slotName)"
        />
      </template>
    </modern-grid>
  </div>
</template>

<style lang="scss">
.base-grid-adapter {
  flex: 1;
  width: 100%;
  min-width: 0;
  height: 100%;
  min-height: 0;

  .base-grid__cell--link {
    width: 100%;
    min-width: 0;
    height: 100%;
    overflow: hidden;
    cursor: pointer;
    color: var(--color-blue-05) !important;
    text-overflow: ellipsis;
    white-space: nowrap;

    > * {
      vertical-align: middle;
    }

    // ellipsis 유지가 필요해 flex/grid 전환 불가 → 전체 높이 인라인 고스트로 세로 중앙 정렬 (Chrome 98 호환)
    &::before {
      display: inline-block;
      height: 100%;
      content: '';
      vertical-align: middle;
    }

    &:hover {
      text-decoration: underline !important;

      .cell-text {
        text-decoration: underline !important;
      }
    }

    .cell-text {
      min-width: 0;
      max-width: 100%;

      @include shortening(inline-block, auto);

      &--icon-right {
        max-width: calc(100% - 4px - 20px);
        margin-right: 4px;
      }
    }
  }

  .selected .base-grid__cell--link {
    color: var(--grid-selected-link-color) !important;
  }

  .base-grid__cell--link:empty {
    cursor: default;
    pointer-events: none;
  }
}
.base-grid__cell-tooltip {
  width: 180px;

  &__chip {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    max-height: 88px;
    padding: 10px 12px;
    overflow-y: auto;
    background-color: var(--menu-list-item-bg-color);

    .tag-item {
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}
</style>
